import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";

const Stars = ({ value, onChange, testid }) => (
  <div className="flex gap-1.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((s) => (
      <button
        key={s}
        type="button"
        onClick={() => onChange(s)}
        data-testid={`${testid}-${s}`}
        className={`transition-transform hover:scale-110 ${s <= (value || 0) ? "text-[#00e599]" : "text-slate-600"}`}
      >
        <Star size={26} fill={s <= (value || 0) ? "currentColor" : "none"} />
      </button>
    ))}
  </div>
);

export default function Survey() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    api
      .get(`/public/survey/${token}`)
      .then(({ data }) => {
        setCtx(data);
        setDone(data.already_submitted);
        const seed = {};
        data.questions.forEach((q) => {
          if (q.type === "stars") seed[q.id] = q.required ? 5 : null;
          if (q.type === "yesno") seed[q.id] = false;
          if (q.type === "text") seed[q.id] = "";
        });
        setAnswers(seed);
      })
      .catch((e) => setError(errText(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(
        Object.entries(answers).filter(([, v]) => v !== null && v !== "")
      );
      await api.post(`/public/survey/${token}`, { answers: payload });
      setDone(true);
      toast.success("Thanks for your feedback!");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  if (error)
    return (
      <Shell>
        <h1 className="font-display text-3xl font-black uppercase" data-testid="survey-error">
          {error}
        </h1>
      </Shell>
    );
  if (!ctx)
    return (
      <Shell>
        <p className="text-sm text-slate-500">Loading survey…</p>
      </Shell>
    );

  return (
    <Shell>
      <div className="max-w-xl rise" data-testid="survey-page">
        <p className="overline">Post-event feedback</p>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight leading-none mt-2">
          {ctx.event.name}
        </h1>
        {done ? (
          <p className="text-sm text-slate-300 mt-8 card p-6" data-testid="survey-thanks">
            Thanks {ctx.attendee_name} — your feedback has been recorded.
          </p>
        ) : (
          <form onSubmit={submit} className="card p-6 mt-8" data-testid="survey-form">
            <p className="text-sm text-slate-400">Hi {ctx.attendee_name}, how did we do?</p>
            {ctx.questions.map((q) => (
              <div className="mt-6" key={q.id} data-testid={`survey-question-${q.id}`}>
                <label className="label">
                  {q.label}
                  {q.required ? " *" : ""}
                </label>
                {q.type === "stars" && (
                  <div className="flex items-center gap-3">
                    <Stars
                      value={answers[q.id]}
                      onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                      testid={`survey-stars-${q.id}`}
                    />
                    <span className="font-mono2 text-[11px] text-slate-500">
                      {answers[q.id] ? `${answers[q.id]}/5` : "not rated"}
                    </span>
                  </div>
                )}
                {q.type === "yesno" && (
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      className="accent-[#00e599] w-4 h-4"
                      data-testid={`survey-yesno-${q.id}`}
                      checked={!!answers[q.id]}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.checked })}
                    />
                    Yes
                  </label>
                )}
                {q.type === "text" && (
                  <textarea
                    rows="4"
                    className="input"
                    required={q.required}
                    data-testid={`survey-text-${q.id}`}
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  />
                )}
              </div>
            ))}
            <button className="btn-primary w-full mt-6" data-testid="survey-submit-btn">
              <Send size={15} /> Submit feedback
            </button>
          </form>
        )}
      </div>
    </Shell>
  );
}
